import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Car,
  CheckCircle,
  FileText,
  Users,
  Users2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../api/index';
import { AlertRow } from '../components/AlertRow';
import { PageHeader } from '../components/PageHeader';
import { StatCard } from '../components/StatCard';
import { useAnalytics } from '../hooks/useAnalytics';

// Skeleton loader component for cards
function CardSkeleton() {
  return <div className="h-24 bg-gradient-to-r from-gray-200 to-gray-100 rounded-lg animate-pulse" />
}

// Loader component for sections
function SectionLoader({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-8 text-gray-600">
      <div className="w-6 h-6 border-3 border-gray-300 border-t-saffron rounded-full animate-spin" />
      <span className="text-sm font-medium">Loading {label}...</span>
    </div>
  )
}

// Section header with status indicator
function SectionHeader({ title, isLoading, icon }: { title: string; isLoading: boolean; icon?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
        {icon}
        {title}
      </h2>
      {isLoading && (
        <div className="w-4 h-4 border-2 border-gray-300 border-t-saffron rounded-full animate-spin" />
      )}
      {!isLoading && (
        <CheckCircle className="w-4 h-4 text-green-500" />
      )}
    </div>
  )
}

export function DashboardPage() {
  const navigate = useNavigate()
  const { trackButtonClick } = useAnalytics()

  // Optimized query options for dashboard data
  // - staleTime: 60s cache (reduce redundant queries)
  // - refetchInterval: 5min (user-initiated or page focus only within this window)
  // - gcTime: 10min (keep data in cache for 10 minutes)
  const dashboardQueryOptions = {
    refetchOnMount: false as const,           // Don't refetch on mount if data exists
    refetchInterval: 5 * 60 * 1000,           // Refetch every 5 minutes
    staleTime: 60 * 1000,                     // Data is fresh for 1 minute
    gcTime: 10 * 60 * 1000,                   // Keep cache for 10 minutes
    refetchOnWindowFocus: false as const,     // Don't refetch when window regains focus
  }

  // Summary query - KPI cards section
  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: () => dashboardAPI.getSummary().then((r) => r.data),
    ...dashboardQueryOptions,
  })

  // Alerts query - Recent expiry alerts section
  const { data: alerts, isLoading: alertsLoading } = useQuery({
    queryKey: ['expiry-alerts'],
    queryFn: () => dashboardAPI.getExpiryAlerts({ limit: 20 }).then((r) => r.data),
    ...dashboardQueryOptions,
  })

  const today = new Date().toLocaleDateString('en-IN', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  const totalDocs = summary
    ? summary.total_licences +
      summary.total_insurances +
      summary.total_pucs +
      summary.total_fitness +
      summary.total_road_taxes +
      summary.total_env_taxes
    : 0

  return (
    <div>
      <PageHeader title="Dashboard" subtitle={today} />

      {/* SECTION 1: Summary KPI Cards */}
      <section className="mb-8">
        <SectionHeader title="Key Metrics" isLoading={summaryLoading} />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {summaryLoading ? (
            <>
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </>
          ) : summary ? (
            <>
              <StatCard
                title="Customers"
                value={summary.total_customers}
                icon={<Users className="w-6 h-6" />}
                color="bg-blue-500"
                onClick={() => {
                  trackButtonClick('View Customers', { destination: '/customers' })
                  navigate('/customers')
                }}
              />
              <StatCard
                title="Vehicles"
                value={summary.total_vehicles}
                icon={<Car className="w-6 h-6" />}
                color="bg-green-500"
                onClick={() => {
                  trackButtonClick('View Vehicles', { destination: '/vehicles' })
                  navigate('/vehicles')
                }}
              />
              <StatCard
                title="Team Members"
                value={summary.total_agents}
                icon={<Users2 className="w-6 h-6" />}
                color="bg-purple-500"
                onClick={() => {
                  trackButtonClick('View Team', { destination: '/team' })
                  navigate('/team')
                }}
              />
              <StatCard
                title="Total Documents"
                value={totalDocs}
                icon={<FileText className="w-6 h-6" />}
                color="bg-orange-500"
              />
            </>
          ) : null}
        </div>
      </section>

      {/* SECTION 2: Recent Expiry Alerts Table */}
      <section className="mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <SectionHeader title="Recent Expiry Alerts" isLoading={alertsLoading} icon={<AlertTriangle className="w-5 h-5 text-danger" />} />
          </div>
          {alertsLoading ? (
            <SectionLoader label="expiry alerts" />
          ) : alerts?.alerts && alerts.alerts.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">
                      Vehicle
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">
                      Reference
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">
                      Expiry Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">
                      Days Left
                    </th>
                    <th className="px-6 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {alerts.alerts.map((alert) => (
                    <AlertRow key={alert.record_id} alert={alert} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center">
              <div className="text-sage text-4xl mb-4">✓</div>
              <p className="text-gray-600 font-medium">All documents are up to date!</p>
            </div>
          )}
        </div>
      </section></div>
  )
}
